/**
 * useTenants Hook
 * React Query hook for fetching and managing user's tenants
 *
 * Fetches from both Clerk orgs endpoint (new) and legacy /user/tenants endpoint
 * for backward compatibility during migration.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth as useClerkAuth } from "@clerk/react-router";
import apiClient from "@/lib/api-client";
import { Tenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";

interface TenantsQueryResult {
  tenants: Tenant[];
  dbUserId?: string;
}

interface GetTenantsResponse {
  tenants: Tenant[];
  dbUserId?: string;
}

interface ClerkOrgMapping {
  tenantId: string;
  clerkOrgId: string;
  clerkOrgName: string;
  clerkOrgSlug: string;
  userRole: string;
  tenantType?: string;
}

interface GetClerkOrgsResponse {
  organizations: ClerkOrgMapping[];
  dbUserId?: string;
}

/**
 * Fetch the signed-in user's tenants (Clerk orgs merged with legacy tenants).
 *
 * Keyed on the active Clerk org (sv-hzvy): a new user's list is first fetched
 * during sign-up, before Clerk's choose-organization task creates their org, and
 * would otherwise stay cached-empty for `staleTime` — leaving /assessments/new on
 * "No organization selected". A new active org is a new key, so it re-fetches.
 *
 * @returns The merged tenants, the DB user id, loading state and any error.
 */
export function useTenants() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { orgId } = useClerkAuth();

  const { data, isLoading, error } = useQuery<TenantsQueryResult, Error>({
    queryKey: ["tenants", orgId ?? null],
    queryFn: async () => {
      // Fetch Clerk orgs (new system)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clerkOrgsResponse = await apiClient.GET("/clerk/orgs" as any);

      // Fetch legacy tenants (for backward compatibility)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const legacyTenantsResponse = await apiClient.GET("/user/tenants" as any);

      // Handle errors
      if (clerkOrgsResponse.error && legacyTenantsResponse.error) {
        throw new Error("Failed to fetch tenants");
      }

      const clerkData = clerkOrgsResponse.data as GetClerkOrgsResponse;
      const legacyData = legacyTenantsResponse.data as GetTenantsResponse;
      const clerkOrgs = clerkData?.organizations || [];
      const legacyTenants = legacyData?.tenants || [];

      // Convert Clerk orgs to Tenant format
      const tenantsFromClerk: Tenant[] = clerkOrgs.map((org) => ({
        id: org.tenantId,
        name: org.clerkOrgName,
        slug: org.clerkOrgSlug,
        memberRole: org.userRole as "owner" | "admin" | "member" | "viewer",
        tenantType: (org.tenantType as "company" | "investor") ?? "company",
        clerkOrgId: org.clerkOrgId,
        createdAt: new Date().toISOString(), // Not available from Clerk API
        updatedAt: new Date().toISOString(), // Not available from Clerk API
      }));

      // Merge tenants, prioritizing Clerk-linked ones and removing duplicates
      const tenantMap = new Map<string, Tenant>();

      // Add legacy tenants first (ensure tenantType has a fallback)
      legacyTenants.forEach((tenant) => {
        tenantMap.set(tenant.id, {
          ...tenant,
          tenantType: tenant?.tenantType ?? "company",
        });
      });

      // Override with Clerk orgs (they have more up-to-date info)
      tenantsFromClerk.forEach((tenant) => {
        tenantMap.set(tenant.id, tenant);
      });

      const mergedTenants = Array.from(tenantMap.values());

      return {
        tenants: mergedTenants,
        dbUserId: clerkData?.dbUserId ?? legacyData?.dbUserId,
      };
    },
    // Only fetch tenants after authentication is complete
    enabled: isAuthenticated && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    tenants: data?.tenants || [],
    dbUserId: data?.dbUserId,
    isLoading: authLoading || isLoading,
    error,
  };
}
